import { createContext, useContext, useEffect, useState, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useToast } from "./ToastContext";

const AuthContext = createContext();

// Firebase's default persistence keeps a session alive forever — the
// refresh token never expires on its own, so a login today is still valid
// in five years unless the user explicitly signs out. Two independent caps
// on top of that: an absolute expiry (defense in depth if a session token
// ever leaks — an attacker's access still runs out) and a shorter idle
// timeout (protects shared/public computers where someone forgets to sign
// out). Both are enforced client-side by simply calling signOut(); nothing
// server-side trusts the client's clock for anything security-sensitive.
const SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_START_KEY = "tb_session_started_at";
const CHECK_INTERVAL_MS = 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        // Stamp once per sign-in, not on every token refresh (Firebase
        // fires this callback for those too) — only set it if this is a
        // genuinely new session.
        if (!localStorage.getItem(SESSION_START_KEY)) {
          localStorage.setItem(SESSION_START_KEY, String(Date.now()));
        }
      } else {
        localStorage.removeItem(SESSION_START_KEY);
      }
      setUser(currentUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Absolute session expiry — checked on an interval, not just at sign-in,
  // so a tab left open past the limit still gets signed out eventually.
  useEffect(() => {
    if (!user) return;

    const checkExpiry = () => {
      const startedAt = Number(localStorage.getItem(SESSION_START_KEY));
      if (startedAt && Date.now() - startedAt > SESSION_MAX_AGE_MS) {
        signOut(auth)
          .then(() => toast.info("Your session expired — please sign in again."))
          .catch(() => {});
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, toast]);

  // Idle timeout — any real interaction resets the clock. Typing itself
  // fires keydown, so an active typing test never trips this; it only
  // fires when a tab is genuinely left untouched.
  useEffect(() => {
    if (!user) return;

    lastActivityRef.current = Date.now();
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
        signOut(auth)
          .then(() => toast.info("Signed out due to inactivity."))
          .catch(() => {});
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActive));
      clearInterval(interval);
    };
  }, [user, toast]);

  return (
    // Render immediately rather than blocking on Firebase Auth's initial
    // handshake — that resolves through a slow iframe helper + Google API
    // round trip (2+ seconds on a cold load), during which the ENTIRE app
    // used to render nothing at all, regardless of page or auth state.
    // Consumers already treat `user: null` as "not signed in yet", which is
    // the correct default before the real state is known. `loading` is
    // still exposed for any consumer that genuinely needs to distinguish
    // "definitely signed out" from "haven't checked yet".
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);